<template>
  <div class="flex flex-col bg-[#f8fafc] gap-4">
    <div class="bg-white rounded-xl shadow-sm border border-slate-200 border-t-4 border-t-[#0d4169] flex flex-col pt-3">
      <div class="relative z-30 px-6 pb-3 flex flex-col gap-4 border-b border-slate-100 bg-white">
        <h2 class="text-base font-bold text-[#0d4169] text-left m-0">HR</h2>
      </div>

      <div class="relative z-0 px-4 sm:px-6 py-4 min-w-0">
        <div class="bg-white shadow-sm border border-slate-200 overflow-hidden min-w-0">
          <div class="px-4 sm:px-6 pt-2 pb-1">
            <div class="flex items-baseline gap-2 flex-wrap">
              <h2 class="text-base font-bold text-[#0d4169] text-left">
                Annual Leaving
              </h2>
            </div>
          </div>

          <form class="px-4 sm:px-6 pt-2" @submit.prevent="findEmployees">
            <div class="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 items-end gap-3 min-w-0">
              <div class="flex flex-col gap-1 min-w-0">
                <label class="text-[0.78rem] font-semibold text-[#1e293b] text-left">Find by ID</label>
                <input
                  v-model.trim="filters.id"
                  type="text"
                  inputmode="numeric"
                  class="w-full min-w-0 px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm bg-white"
                  placeholder="Employee ID"
                >
              </div>

              <div class="flex flex-col gap-1 min-w-0">
                <label class="text-[0.78rem] font-semibold text-[#1e293b] text-left">Find by Name</label>
                <input
                  v-model.trim="filters.name"
                  type="text"
                  class="w-full min-w-0 px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm bg-white"
                  placeholder="Employee name"
                >
              </div>

              <div class="flex flex-col gap-1 min-w-0">
                <label class="text-[0.78rem] font-semibold text-[#1e293b] text-left">Find By Phone</label>
                <input
                  v-model.trim="filters.phone"
                  type="text"
                  inputmode="tel"
                  class="w-full min-w-0 px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm bg-white"
                  placeholder="Employee phone"
                >
              </div>

              <div class="flex justify-stretch sm:justify-start min-w-0">
                <button
                  type="submit"
                  :disabled="loading || !canFindAnnualLeaving"
                  class="w-full sm:w-auto min-w-max px-8 py-1.5 bg-[#0d4169] text-white text-sm font-bold rounded-md shadow-md hover:bg-[#0a3354] transition-all active:scale-95 whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  <div
                    v-if="loading"
                    class="h-5 w-5 border-2 border-white/30 border-t-transparent rounded-full animate-spin"
                  ></div>
                  <span v-if="!loading">Find</span>
                </button>
              </div>
            </div>
            <p v-if="errorMessage" class="mt-2 text-xs text-red-600 text-left">{{ errorMessage }}</p>
          </form>

          <div class="px-4 sm:px-6 pb-6 pt-4 min-w-0">
            <div
              class="w-full max-w-full overscroll-x-contain"
              :class="loading ? 'overflow-x-hidden' : 'overflow-x-auto'"
            >
              <table class="w-full min-w-[1100px] border-collapse bg-white rounded-xl shadow-sm">
                <thead>
                  <tr>
                    <th>No.</th>
                    <th @click="sortBy('id')" class="sort-col text-[12px]">
                      Id No <span v-if="sortKey === 'id'">{{ sortOrder === 'asc' ? '↑' : '↓' }}</span>
                    </th>
                    <th @click="sortBy('full_name')" class="sort-col text-[12px]">
                      Employee Name <span v-if="sortKey === 'full_name'">{{ sortOrder === 'asc' ? '↑' : '↓' }}</span>
                    </th>
                    <th @click="sortBy('role')" class="sort-col text-[12px]">
                      Role <span v-if="sortKey === 'role'">{{ sortOrder === 'asc' ? '↑' : '↓' }}</span>
                    </th>
                    <th @click="sortBy('start_date')" class="sort-col text-[12px]">
                      Date of Employment <span v-if="sortKey === 'start_date'">{{ sortOrder === 'asc' ? '↑' : '↓' }}</span>
                    </th>
                    <th @click="sortBy('service_month')" class="sort-col text-[12px] text-center">
                      Service Month <span v-if="sortKey === 'service_month'">{{ sortOrder === 'asc' ? '↑' : '↓' }}</span>
                    </th>
                    <th @click="sortBy('total_leave')" class="sort-col text-[12px] text-center">
                      Total Leave <span v-if="sortKey === 'total_leave'">{{ sortOrder === 'asc' ? '↑' : '↓' }}</span>
                    </th>
                    <th @click="sortBy('used_leave')" class="sort-col text-[12px] text-center">
                      Used Leave <span v-if="sortKey === 'used_leave'">{{ sortOrder === 'asc' ? '↑' : '↓' }}</span>
                    </th>
                    <th @click="sortBy('current_available')" class="sort-col text-[12px] text-center">
                      Current Available <span v-if="sortKey === 'current_available'">{{ sortOrder === 'asc' ? '↑' : '↓' }}</span>
                    </th>
                    <th @click="sortBy('status')" class="sort-col text-[12px]">
                      Status <span v-if="sortKey === 'status'">{{ sortOrder === 'asc' ? '↑' : '↓' }}</span>
                    </th>
                    <th class="text-[12px]">Action</th>
                  </tr>
                </thead>
                <tbody class="text-[12px]">
                  <tr
                    v-for="(user, index) in sortedUsers"
                    v-show="!loading"
                    :key="user.id"
                    class="hover:bg-slate-100/50 transition-colors border-b"
                  >
                    <td class="font-medium text-slate-500">{{ index + 1 }}</td>
                    <td class="font-bold text-[#0d4169]">{{ user.id }}</td>
                    <td class="font-bold text-[#0d4169]">{{ user.full_name }}</td>
                    <td>
                      <span
                        v-if="user.role"
                        class="px-2 py-0.5 bg-slate-100 rounded text-[10px] font-bold text-slate-600 border border-slate-200 uppercase tracking-tighter whitespace-nowrap inline-block"
                      >
                        {{ formatRole(user.role) }}
                      </span>
                      <span v-else class="text-slate-400">—</span>
                    </td>
                    <td class="text-slate-600 font-medium">{{ formatDate(user.start_date) || '—' }}</td>
                    <td class="text-slate-600 font-medium text-center">{{ formatServiceMonth(user.service_month) }}</td>
                    <td class="text-slate-600 font-medium text-center">{{ user.total_leave }}</td>
                    <td class="text-slate-600 font-medium text-center">{{ user.used_leave }}</td>
                    <td class="text-slate-600 font-medium text-center">{{ user.current_available }}</td>
                    <td>
                      <span class="status-pill" :class="user.status === 1 ? 'active' : 'inactive'">
                        {{ user.status === 1 ? 'Active' : 'Inactive' }}
                      </span>
                    </td>
                    <td class="actions">
                      <template v-if="user.pending_leave_id">
                        <button
                          type="button"
                          class="edit-btn"
                          title="Edit"
                          :disabled="!canEditAnnualLeaving"
                          @click="onEdit(user)"
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          class="approve-btn"
                          title="Approve"
                          :disabled="!canApproveAnnualLeaving"
                          @click="onApprove(user)"
                        >
                          Approve
                        </button>
                      </template>
                      <button
                        v-else
                        type="button"
                        class="request-btn"
                        title="Request"
                        :disabled="!canRequestAnnualLeaving"
                        @click="onRequest(user)"
                      >
                        Request
                      </button>
                    </td>
                  </tr>
                  <tr v-if="loading">
                    <td colspan="11" class="text-center py-10">
                      <div class="flex items-center justify-center">
                        <div class="h-8 w-8 border-4 border-gray-300 border-t-transparent rounded-full animate-spin"></div>
                      </div>
                    </td>
                  </tr>
                  <tr v-else-if="hasSearched && sortedUsers.length === 0">
                    <td colspan="11" style="text-align:center; color:#94a3b8; padding: 48px 15px; font-style: italic;">
                      No employees found.
                    </td>
                  </tr>
                  <tr v-else-if="!hasSearched">
                    <td colspan="11" style="text-align:center; color:#94a3b8; padding: 48px 15px; font-style: italic;">
                      Enter ID, name, and/or phone, then click Find.
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <AnnualLeaveRequestModal
    :is-open="showRequestModal"
    :employee="requestEmployee"
    :mode="modalMode"
    :leave-id="editLeaveId"
    @close="closeRequestModal"
    @saved="onLeaveSaved"
  />
</template>

<script setup>
import { computed, ref } from 'vue'
import { useStore } from 'vuex'
import { apiFetch } from '../../../utils/api'
import AnnualLeaveRequestModal from './AnnualLeaveRequestModal.vue'

const PRIVILEGE_NAME = 'HR Leaving Request'
const store = useStore()
const currentUser = computed(() => store.getters.currentUser || store.state.user || {})

const userPrivileges = computed(() => {
  const raw = currentUser.value?.privilege
  if (!raw) return {}
  if (typeof raw === 'string') {
    try {
      return JSON.parse(raw)
    } catch {
      return {}
    }
  }
  return raw
})

const hasAllPrivilege = computed(() => {
  return userPrivileges.value?.All === true || userPrivileges.value?.all === true
})

function getPrivilegeEntry(key) {
  const priv = userPrivileges.value || {}
  if (!key) return null
  if (priv?.[key]) return priv[key]

  const normalizedNeedle = String(key).trim().toLowerCase()
  const foundKey = Object.keys(priv).find((k) => String(k).trim().toLowerCase() === normalizedNeedle)
  return foundKey ? priv[foundKey] : null
}

function getActionFlag(entry, action) {
  if (!entry || typeof entry !== 'object') return false
  if (!action) return false
  if (entry[action] !== undefined) return !!entry[action]

  const a = String(action)
  const candidates = [
    a.toLowerCase(),
    a.toUpperCase(),
    a.charAt(0).toUpperCase() + a.slice(1).toLowerCase()
  ]
  for (const k of candidates) {
    if (entry[k] !== undefined) return !!entry[k]
  }
  return false
}

function hasAnnualLeavingPrivilege(action) {
  if (hasAllPrivilege.value) return true
  return getActionFlag(getPrivilegeEntry(PRIVILEGE_NAME), action)
}

const canFindAnnualLeaving = computed(() => hasAnnualLeavingPrivilege('Find'))
const canEditAnnualLeaving = computed(() => hasAnnualLeavingPrivilege('Edit'))
const canRequestAnnualLeaving = computed(() => hasAnnualLeavingPrivilege('Request'))
const canApproveAnnualLeaving = computed(() => hasAnnualLeavingPrivilege('Approve'))

const filters = ref({
  id: '',
  name: '',
  phone: '',
})

const users = ref([])
const loading = ref(false)
const errorMessage = ref('')
const hasSearched = ref(false)
const sortKey = ref('full_name')
const sortOrder = ref('asc')
const showRequestModal = ref(false)
const requestEmployee = ref(null)
const modalMode = ref('create')
const editLeaveId = ref(null)

const numericSortKeys = new Set([
  'id',
  'service_month',
  'total_leave',
  'used_leave',
  'current_available',
  'status',
])

const enrichedUsers = computed(() => {
  return users.value.map((user) => {
    const total = Number(user.total_leave ?? 0) || 0
    const used = Number(user.used_leave ?? 0) || 0
    const available = total - used
    return {
      ...user,
      service_month: calcServiceMonths(user.start_date),
      total_leave: total,
      used_leave: used,
      current_available: Number.isFinite(available) ? Number(available.toFixed(2)) : 0,
    }
  })
})

const sortedUsers = computed(() => {
  const result = [...enrichedUsers.value]
  const modifier = sortOrder.value === 'asc' ? 1 : -1
  const key = sortKey.value

  result.sort((a, b) => {
    if (numericSortKeys.has(key)) {
      const valA = a[key] == null || a[key] === '' ? -Infinity : Number(a[key])
      const valB = b[key] == null || b[key] === '' ? -Infinity : Number(b[key])
      if (valA < valB) return -1 * modifier
      if (valA > valB) return 1 * modifier
      return 0
    }

    const valA = (a[key] ?? '').toString().toLowerCase()
    const valB = (b[key] ?? '').toString().toLowerCase()
    if (valA < valB) return -1 * modifier
    if (valA > valB) return 1 * modifier
    return 0
  })

  return result
})

function sortBy(key) {
  if (sortKey.value === key) {
    sortOrder.value = sortOrder.value === 'asc' ? 'desc' : 'asc'
  } else {
    sortKey.value = key
    sortOrder.value = 'asc'
  }
}

function formatRole(role) {
  if (!role) return ''
  const parts = String(role).split(',').map((p) => p.trim()).filter(Boolean)
  if (!parts.length) return ''
  return parts.length > 1 ? `${parts[0]} +${parts.length - 1}` : parts[0]
}

function formatDate(value) {
  if (!value) return ''
  const s = String(value)
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) {
    const [y, m, d] = s.slice(0, 10).split('-')
    return `${d}/${m}/${y}`
  }
  const dt = new Date(value)
  if (Number.isNaN(dt.getTime())) return s
  const dd = String(dt.getDate()).padStart(2, '0')
  const mm = String(dt.getMonth() + 1).padStart(2, '0')
  const yyyy = dt.getFullYear()
  return `${dd}/${mm}/${yyyy}`
}

function calcServiceMonths(startDate) {
  if (!startDate) return null
  const raw = String(startDate).slice(0, 10)
  if (!/^\d{4}-\d{2}-\d{2}$/.test(raw)) return null

  const [y, m, d] = raw.split('-').map(Number)
  const start = new Date(y, m - 1, d)
  if (Number.isNaN(start.getTime())) return null

  const now = new Date()
  let months =
    (now.getFullYear() - start.getFullYear()) * 12 + (now.getMonth() - start.getMonth())
  if (now.getDate() < start.getDate()) months -= 1
  return Math.max(0, months)
}

function formatServiceMonth(value) {
  if (value === null || value === undefined || value === '') return '—'
  return String(value)
}

function onRequest(user) {
  if (!canRequestAnnualLeaving.value) return
  modalMode.value = 'create'
  editLeaveId.value = null
  requestEmployee.value = { ...user }
  showRequestModal.value = true
}

function onEdit(user) {
  if (!canEditAnnualLeaving.value) return
  if (!user?.pending_leave_id) return
  modalMode.value = 'edit'
  editLeaveId.value = user.pending_leave_id
  requestEmployee.value = { ...user }
  showRequestModal.value = true
}

function onApprove(user) {
  if (!canApproveAnnualLeaving.value) return
  if (!user?.pending_leave_id) return
  modalMode.value = 'approve'
  editLeaveId.value = user.pending_leave_id
  requestEmployee.value = { ...user }
  showRequestModal.value = true
}

function closeRequestModal() {
  showRequestModal.value = false
  requestEmployee.value = null
  modalMode.value = 'create'
  editLeaveId.value = null
}

async function onLeaveSaved() {
  await findEmployees({ silent: true })
}

async function findEmployees(options = {}) {
  const silent = !!options.silent
  if (!silent && !canFindAnnualLeaving.value) return
  if (!silent) errorMessage.value = ''
  const name = String(filters.value.name || '').trim()
  const id = String(filters.value.id || '').trim()
  const phone = String(filters.value.phone || '').trim()

  if (!name && !id && !phone) {
    errorMessage.value = 'Enter an ID, name, and/or phone to find an employee.'
    return
  }

  loading.value = true
  hasSearched.value = true

  try {
    const params = new URLSearchParams()
    if (id) params.set('id', id)
    if (name) params.set('name', name)
    if (phone) params.set('phone', phone)

    const [employeesRes] = await Promise.all([
      apiFetch(`/hr/employee/annual-leaving?${params.toString()}`),
    ])

    if (!employeesRes.ok) {
      const err = await employeesRes.json().catch(() => ({}))
      throw new Error(err.message || 'Failed to find employees')
    }

    const employeeData = await employeesRes.json().catch(() => ({}))
    users.value = Array.isArray(employeeData?.employees) ? employeeData.employees : []
  } catch (err) {
    console.error('Error finding annual leaving employees:', err)
    users.value = []
    errorMessage.value = err.message || 'Failed to find employees'
  } finally {
    loading.value = false
  }
}
</script>

<style scoped>
table {
  font-family: 'Segoe UI', sans-serif;
  font-size: 11px;
  line-height: 1.15;
  color: #1e293b;
}

th, td {
  padding: 3px 15px;
  text-align: left;
  border-bottom: 1px solid #e2e8f0;
  white-space: nowrap;
}

th {
  background: #f1f5f9;
  cursor: default;
  font-weight: 600;
}

th.sort-col {
  cursor: pointer;
}

th.sort-col:hover {
  background: #e2e8f0;
}

th.text-center,
td.text-center {
  text-align: center !important;
}

tbody tr:hover {
  background: #f8fafc;
}

.status-pill {
  padding: 2px 8px;
  border-radius: 20px;
  font-size: 10px;
  font-weight: bold;
}

.active { background: #dcfce7; color: #166534; }
.inactive { background: #fee2e2; color: #991b1b; }

.actions {
  display: flex;
  align-items: center;
  gap: 8px;
  border-bottom: none;
}

.request-btn,
.approve-btn,
.edit-btn {
  color: #fff;
  border: none;
  border-radius: 6px;
  padding: 4px 10px;
  font-size: 11px;
  font-weight: 700;
  cursor: pointer;
  transition: background-color 0.15s ease, transform 0.1s ease;
}

.request-btn {
  background: #0d4169;
}

.request-btn:hover {
  background: #0a3354;
}

.edit-btn {
  background: #0369a1;
}

.edit-btn:hover {
  background: #0284c7;
}

.approve-btn {
  background: #15803d;
}

.approve-btn:hover {
  background: #166534;
}

.request-btn:active,
.approve-btn:active,
.edit-btn:active {
  transform: scale(0.97);
}

.approve-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
</style>
